import { Fragment, Mark, Node, Slice } from "prosemirror-model";
import { Transaction } from "prosemirror-state";
import {
  AddMarkStep,
  AttrStep,
  RemoveMarkStep,
  ReplaceAroundStep,
  ReplaceStep,
  Transform,
} from "prosemirror-transform";
import Delta from "quill-delta";

import { toDeltaPos } from "./pos";

function markAttributes(marks: readonly Mark[]) {
  const attrs: Record<string, unknown> = {};
  for (const m of marks) {
    const markName = m.type.name;
    if (markName === "bold") attrs.bold = true;
    else if (markName === "italic") attrs.italic = true;
    else if (markName === "underline") attrs.underline = true;
    else if (markName === "link") attrs.link = m.attrs.href;
    else if (markName === "color") attrs.color = m.attrs.color;
    else throw new Error(`Unexpected mark type in toDelta (${markName})`);
  }
  return attrs;
}

function blockAttributes(block: Node) {
  const attrs: Record<string, unknown> = {};
  if (block.type.name === "quote") attrs.blockquote = true;
  else if (block.type.name === "heading") attrs.header = block.attrs.level;
  else if (block.type.name === "ordered_list_item") attrs.list = "ordered";
  else if (block.type.name === "bullet_list_item") attrs.list = "bullet";
  else if (block.type.name === "code_block") attrs["code-block"] = "plain";
  else if (block.type.name !== "paragraph")
    throw new Error(`Unexpected block type in toDelta (${block.type.name})`);
  if (block.attrs.indent > 0) attrs.indent = block.attrs.indent;
  if (block.attrs.align !== "left") attrs.align = block.attrs.align;
  return attrs;
}

/** Emits inline content, splitting text at \n and tagging each newline with codeAttrs. */
function codeContentToDelta(content: Fragment, codeAttrs: Record<string, unknown>, delta: Delta) {
  content.forEach((child) => {
    if (child.isText) {
      const lines = child.text!.split("\n");
      const attrs = child.marks.length ? markAttributes(child.marks) : undefined;
      for (let i = 0; i < lines.length; i += 1) {
        if (i > 0) delta.insert("\n", codeAttrs);
        if (lines[i].length > 0) delta.insert(lines[i], attrs);
      }
    } else {
      inlineToDelta(child, delta);
    }
  });
}

/**
 * Emits a full code_block node as delta ops, including the trailing line-ending.
 * The trailing \n is only emitted when the block has content. Empty code blocks
 * get their line-ending from the caller (docToDelta) instead, because emitting
 * it here would also fire for empty code_block wrapper nodes in sliceToDelta,
 * producing a spurious \n at the opening boundary of the slice.
 */
function codeBlockToDelta(block: Node, delta: Delta): void {
  const codeAttrs = blockAttributes(block);
  codeContentToDelta(block.content, codeAttrs, delta);
  if (block.childCount > 0) delta.insert("\n", codeAttrs);
}

function inlineToDelta(node: Node, delta: Delta) {
  const attributes = markAttributes(node.marks);
  if (node.isText) {
    delta.insert(node.text!, attributes);
  } else if (node.type.name === "image") {
    if (node.attrs.width) attributes.width = String(node.attrs.width);
    if (node.attrs.alt) attributes.alt = node.attrs.alt;
    if (node.attrs.isDecorative !== null) attributes.dec = node.attrs.isDecorative;
    delta.insert({ image: node.attrs.src }, attributes);
  } else if (node.type.name === "formula") {
    delta.insert({ formula: node.attrs.formula }, attributes);
  } else {
    throw new RangeError(`Unexpected inline node type in toDelta ${node.type.name}`);
  }
}

export function docToDelta(doc: Node) {
  const delta = new Delta();
  for (let i = 0; i < doc.childCount; i += 1) {
    const block = doc.child(i);
    if (block.type.name === "code_block") {
      codeBlockToDelta(block, delta);
      if (block.childCount === 0) delta.insert("\n", blockAttributes(block));
    } else {
      for (let j = 0; j < block.childCount; j += 1) inlineToDelta(block.child(j), delta);
      delta.insert("\n", blockAttributes(block));
    }
  }
  return delta;
}

function sliceToDelta(slice: Slice, activeParam: Node | null, delta: Delta) {
  if (slice.size === 0) return activeParam;
  const first = slice.content.child(0);
  const inline = first.isInline
    ? slice.content
    : slice.openStart && slice.openEnd && slice.content.childCount === 1
      ? slice.content.child(0).content
      : null;
  let active = activeParam;
  if (inline) {
    if (active?.type.name === "code_block") {
      codeContentToDelta(inline, blockAttributes(active), delta);
      return active;
    }
    for (let i = 0; i < inline.childCount; i += 1) inlineToDelta(inline.child(i), delta);
    return active;
  }
  if (!slice.openStart && active) {
    delta.insert("\n", blockAttributes(active));
    active = null;
  }
  for (let i = 0; i < slice.content.childCount; i += 1) {
    const block = slice.content.child(i);
    if (block.type.name === "code_block") {
      codeBlockToDelta(block, delta);
    } else {
      for (let j = 0; j < block.childCount; j += 1) inlineToDelta(block.child(j), delta);
    }
    if (i < slice.content.childCount - 1 || !slice.openEnd) {
      // Code block already emits a \n per line in codeBlockToDelta when it has content; skip extra closing newline.
      if (block.type.name !== "code_block" || block.childCount === 0) {
        delta.insert("\n", blockAttributes(active || block));
      }
      active = null;
    } else {
      active = block;
    }
  }
  return active;
}

function splitSlice(slice: Slice, at: number) {
  let innerDepth = 0;
  const cut = at + slice.openStart;
  for (let frag = slice.content, off = cut; ; ) {
    const inner = childAt(frag, off);
    if (!inner.node || inner.node.isText) break;
    innerDepth += 1;
    frag = inner.node.content;
    off -= inner.pos + 1;
  }
  return {
    before: new Slice(slice.content.cut(0, cut), slice.openStart, innerDepth),
    after: new Slice(slice.content.cut(cut), innerDepth, slice.openEnd),
  };
}

function childAt(parent: Node | Fragment, pos: number) {
  for (let i = 0, off = 0; ; i += 1) {
    if (off === pos) return { node: null, pos: off, index: i };
    const ch = parent.child(i);
    const end = off + ch.nodeSize;
    if (end > pos) return { node: ch, pos: off, index: i };
    off = end;
  }
}

export function stepsToDelta(tr: Transform | Transaction) {
  let delta = new Delta();
  for (let i = 0; i < tr.steps.length; i += 1) {
    const step = tr.steps[i];
    const doc = tr.docs[i];
    const stepDelta = new Delta();
    if (step instanceof ReplaceStep) {
      const start = toDeltaPos(doc, step.from);
      const end = toDeltaPos(doc, step.to);
      stepDelta.retain(start);
      stepDelta.delete(end - start);
      const active = sliceToDelta(step.slice, childAt(doc, step.from).node, stepDelta);
      if (active) {
        const after = childAt(doc, step.to);
        const diff =
          after.node &&
          Delta.AttributeMap.diff(blockAttributes(after.node), blockAttributes(active));
        if (diff) {
          stepDelta.retain(after.pos + 1 + after.node.content.size - step.to);
          stepDelta.retain(1, diff);
        }
      }
    } else if (step instanceof ReplaceAroundStep) {
      const start = toDeltaPos(doc, step.from);
      const end = toDeltaPos(doc, step.to);
      const gapStart = toDeltaPos(doc, step.gapFrom);
      const gapEnd = toDeltaPos(doc, step.gapTo);
      const { before, after } = splitSlice(step.slice, step.insert);
      stepDelta.retain(start);
      stepDelta.delete(gapStart - start);
      let active = sliceToDelta(before, childAt(doc, step.from).node, stepDelta);
      stepDelta.retain(gapEnd - gapStart);
      const endBlock = childAt(doc, step.gapTo);
      stepDelta.delete(end - gapEnd);
      active = sliceToDelta(
        after,
        endBlock.pos >= step.gapFrom ? endBlock.node : active,
        stepDelta,
      );
      if (active) {
        const afterBlock = childAt(doc, step.to);
        const diff =
          afterBlock.node &&
          Delta.AttributeMap.diff(blockAttributes(afterBlock.node), blockAttributes(active));
        if (diff) {
          stepDelta.retain(afterBlock.pos + afterBlock.node.nodeSize - 1 - step.to);
          stepDelta.retain(1, diff);
        }
      }
    } else if (step instanceof AddMarkStep || step instanceof RemoveMarkStep) {
      const start = toDeltaPos(doc, step.from);
      const end = toDeltaPos(doc, step.to);
      stepDelta.retain(start);
      const attrs = markAttributes([step.mark]);
      stepDelta.retain(
        end - start,
        step instanceof AddMarkStep ? attrs : Delta.AttributeMap.diff(attrs, {}),
      );
    } else if (step instanceof AttrStep) {
      const node = doc.nodeAt(step.pos)!;
      let name = step.attr;
      let value = step.value ?? null;
      if (node.isBlock) {
        const endPos = toDeltaPos(doc, step.pos + node.nodeSize - 1);
        stepDelta.retain(endPos);
        if (name === "align" && value === "left") value = null;
        else if (name === "indent" && !value) value = null;
        else if (name === "level") name = "header";
        stepDelta.retain(1, { [name]: value });
      } else {
        stepDelta.retain(toDeltaPos(doc, step.pos));
        if (
          (node.type.name === "image" && name === "src") ||
          (node.type.name === "formula" && name === "formula")
        ) {
          inlineToDelta(
            node.type.create({ ...node.attrs, [name]: value }, null, node.marks),
            stepDelta,
          );
          stepDelta.delete(1);
        } else {
          if (name === "width" && value !== null) value = String(value);
          if (name === "alt" && value !== null) value = String(value);
          if (name === "isDecorative" && value !== null) value = Boolean(value);

          // Map ProseMirror attribute names to Delta attribute names for nodes.
          const nameMap = { isDecorative: "dec" };
          const deltaName = nameMap[name] ?? name;
          stepDelta.retain(1, { [deltaName]: value });
        }
      }
    } else {
      throw new Error(`Unsupported step type: ${step.constructor.name}`);
    }
    delta = delta.compose(stepDelta);
  }
  return delta;
}
